import { Buffer } from 'buffer';
import { photoAssetsService } from 'services/photoAssetsService';
import { s3Service } from 'services/s3Service';

const DEFAULT_ASSET_DIR = 'user-photo-assets';
const MAX_PERSIST_BATCH_SIZE = 1;
const LARGE_DATA_URL_THRESHOLD = 400000;
const IS_DEV = process.env.NODE_ENV !== 'production';

const isDataUrl = value => typeof value === 'string' && value.startsWith('data:');
const normalizeArray = value => (Array.isArray(value) ? value : []);

const debugLog = (...args) => {
  if (IS_DEV) {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
};

const loadDataUrlAsBlob = async dataUrl => {
  if (!dataUrl || typeof dataUrl !== 'string') return null;
  try {
    const response = await fetch(dataUrl);
    if (response.ok) {
      return await response.blob();
    }
  } catch (error) {
    // ignore
  }
  const [header, body] = dataUrl.split(',');
  const match = header.match(/data:(.*?);base64/);
  const mimeType = match ? match[1] : 'image/jpeg';
  const binary =
    typeof window !== 'undefined' && typeof window.atob === 'function'
      ? window.atob(body)
      : Buffer.from(body, 'base64').toString('binary');
  const len = binary.length;
  const buffer = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) {
    buffer[i] = binary.charCodeAt(i);
  }
  return new Blob([buffer], { type: mimeType });
};

const buildLibraryIndex = library => {
  const index = new Map();
  normalizeArray(library).forEach(entry => {
    if (!entry || typeof entry !== 'object') {
      return;
    }
    const keys = [
      entry.photoRef,
      entry.remotePhotoId,
      entry.cachedRemotePhotoId,
      entry.id,
      entry.dataCacheKey,
      entry.photoCacheKey,
      entry.url,
    ].filter(Boolean);
    keys.forEach(key => {
      if (key && !index.has(key)) {
        index.set(key, entry);
      }
    });
  });
  return index;
};

const resolveLibraryEntry = (libraryIndex, key, fallbackKeys = []) => {
  if (key && libraryIndex.has(key)) {
    return libraryIndex.get(key);
  }
  for (const candidate of fallbackKeys) {
    if (candidate && libraryIndex.has(candidate)) {
      return libraryIndex.get(candidate);
    }
  }
  return null;
};

const extractDataUrl = entry => {
  if (!entry || typeof entry !== 'object') return null;
  return (
    entry.cachedDataUrl ||
    entry.url ||
    entry.originalUrl ||
    entry.preview ||
    entry.thumbnailUrl ||
    null
  );
};

const resolveRemoteUrl = (entry, object) => {
  if (entry && typeof entry === 'object') {
    return (
      entry.persistedAssetUrl ||
      entry.remoteUrl ||
      entry.originalRemoteUrl ||
      entry.originalUrl ||
      entry.url ||
      entry.preview ||
      entry.thumbnailUrl ||
      null
    );
  }
  if (object && typeof object === 'object') {
    return object.photoRemoteUrl || object.src || null;
  }
  return null;
};

const applyAssetToTargets = ({ asset, targets, cache, manifest }) => {
  if (!asset || !asset.url || !Array.isArray(targets)) {
    return;
  }
  targets.forEach(({ obj, key }) => {
    if (!obj) return;
    obj.photoRemoteUrl = asset.url;
    obj.src = asset.url;
    obj.crossOrigin = 'anonymous';
    obj.inlineSrcRemoved = false;
    if (obj.slotDataUrl) delete obj.slotDataUrl;
    if (obj._slotDataUrl) delete obj._slotDataUrl;
    const mapped = {
      key,
      photoRef: asset.photoRef || obj.photoRef || key,
      url: asset.url,
      s3Key: asset.s3Key,
    };
    cache.set(key, mapped);
    manifest.push(mapped);
  });
};

const persistAssetLocally = async ({ payload, getNextFileName }) => {
  if (!payload) {
    return null;
  }
  let blob = null;
  if (payload.dataUrl) {
    blob = await loadDataUrlAsBlob(payload.dataUrl);
  }
  if (!blob && payload.remoteUrl) {
    try {
      const response = await fetch(payload.remoteUrl);
      if (response.ok) {
        blob = await response.blob();
      }
    } catch (error) {}
  }
  if (!blob) {
    return null;
  }
  const result = await s3Service.uploadBlob(blob, {
    dirName: DEFAULT_ASSET_DIR,
    fileName: getNextFileName(),
  });
  return {
    key: payload.photoRef || payload.key,
    photoRef: payload.photoRef || payload.key,
    url: result.url,
    s3Key: result.key,
  };
};

const normalizePayload = async (payload, getNextFileName) => {
  if (!payload || !payload.dataUrl) {
    return payload;
  }
  if (payload.dataUrl.length <= LARGE_DATA_URL_THRESHOLD) {
    return payload;
  }
  const persisted = await persistAssetLocally({ payload, getNextFileName });
  if (persisted?.url) {
    return {
      ...payload,
      dataUrl: null,
      remoteUrl: persisted.url,
      persistedAssetUrl: persisted.url,
    };
  }
  return payload;
};

const buildAssetRequestPayload = ({ key, object, entry, remoteUrl, dataUrl }) => {
  const photoRef = object.photoRef || key;
  if (!photoRef) {
    debugLog('[photo-assets] skipped (missing photoRef)', { key, object });
    return null;
  }
  const payload = {
    key,
    photoRef,
    sessionId: entry?.sessionId || object.sessionId || null,
    cachedSessionId: entry?.cachedSessionId || object.cachedSessionId || null,
    remotePhotoId: entry?.remotePhotoId || object.remotePhotoId || null,
    cachedRemotePhotoId: entry?.cachedRemotePhotoId || object.cachedRemotePhotoId || null,
    remoteUrl: remoteUrl || entry?.persistedAssetUrl || object.photoRemoteUrl || object.src || null,
    photoRemoteUrl: object.photoRemoteUrl || null,
    src: object.src || null,
    dataUrl: dataUrl || object.slotDataUrl || object._slotDataUrl || null,
  };
  return payload;
};

const buildBackendResultIndex = assets => {
  const index = new Map();
  normalizeArray(assets).forEach(asset => {
    if (!asset || !asset.url) return;
    if (asset.photoRef && !index.has(asset.photoRef)) {
      index.set(asset.photoRef, asset);
    }
    if (asset.key && !index.has(asset.key)) {
      index.set(asset.key, asset);
    }
  });
  return index;
};

export const ensureCanvasPhotoAssets = async ({
  canvasJson,
  library,
  uploadPrefix,
  assetCache,
}) => {
  if (!canvasJson) {
    return { json: canvasJson, assets: [] };
  }
  const parsed =
    typeof canvasJson === 'string'
      ? JSON.parse(canvasJson)
      : JSON.parse(JSON.stringify(canvasJson));
  const objects = Array.isArray(parsed?.objects) ? parsed.objects : [];
  if (!objects.length) {
    return { json: parsed, assets: [] };
  }

  const libraryIndex = buildLibraryIndex(library);
  const manifest = [];
  let uploadSequence = 0;
  const cache = assetCache || new Map();
  const pendingAssets = new Map();

  const getNextFileName = () => {
    uploadSequence += 1;
    return `${uploadPrefix}_${uploadSequence}.jpg`;
  };

  for (const obj of objects) {
    if (!obj || typeof obj !== 'object') continue;
    // Skip overlay objects entirely so they are never
    // considered for asset persistence or library ingestion.
    // Although ingestPhotoAssetsIntoLibrary guards against
    // 'overlayImage', filtering here avoids manifest pollution
    // and accidental snapshots entering the gallery.
    if (obj.isOverlay || obj.id === 'overlayImage') continue;
    const key =
      obj.photoRef ||
      obj.photoCacheKey ||
      obj.cachedRemotePhotoId ||
      obj.remotePhotoId ||
      obj.id;
    if (!key) continue;

    if (cache.has(key)) {
      applyAssetToTargets({
        asset: cache.get(key),
        targets: [{ obj, key }],
        cache,
        manifest,
      });
      continue;
    }

    const entry = resolveLibraryEntry(libraryIndex, key, [
      obj.photoRef,
      obj.photoCacheKey,
      obj.remotePhotoId,
    ]);
    const remoteUrl = resolveRemoteUrl(entry, obj);
    if (remoteUrl && remoteUrl.includes(DEFAULT_ASSET_DIR)) {
      const asset = { key, photoRef: obj.photoRef || key, url: remoteUrl };
      applyAssetToTargets({ asset, targets: [{ obj, key }], cache, manifest });
      continue;
    }

    const dataUrl = extractDataUrl(entry);
    let payload = buildAssetRequestPayload({
      key,
      object: obj,
      entry,
      remoteUrl,
      dataUrl,
    });
    if (!payload) {
      continue;
    }

    payload = await normalizePayload(payload, getNextFileName);

    const ref = payload.photoRef || key;
    if (!pendingAssets.has(ref)) {
      pendingAssets.set(ref, { payload, targets: [] });
    }
    pendingAssets.get(ref).targets.push({ obj, key });
  }

  let backendResults = new Map();
  if (pendingAssets.size) {
    const entries = Array.from(pendingAssets.values());
    for (let batch = 0; batch < entries.length; batch += MAX_PERSIST_BATCH_SIZE) {
      const chunk = entries.slice(batch, batch + MAX_PERSIST_BATCH_SIZE);
      try {
        debugLog('[photo-assets] persist request', {
          uploadPrefix,
          assetCount: chunk.length,
        });
        const response = await photoAssetsService.persistDesignAssets({
          uploadPrefix,
          assets: chunk.map(entry => entry.payload),
        });
        const indexed = buildBackendResultIndex(response?.assets || []);
        indexed.forEach((value, key) => backendResults.set(key, value));
        if (Array.isArray(response?.errors) && response.errors.length) {
          console.warn('[photo-assets] backend reported errors', response.errors);
        }
      } catch (error) {
        console.error('persistDesignAssets failed', error);
        chunk.forEach(entry => {
          entry.payload = {
            ...entry.payload,
            dataUrl: entry.payload.dataUrl || entry.payload.src || null,
          };
        });
      }
    }
  }

  const fallbackQueue = [];
  pendingAssets.forEach(({ payload, targets }) => {
    const hit = backendResults.get(payload.photoRef) || backendResults.get(payload.key);
    if (hit && hit.url) {
      applyAssetToTargets({ asset: hit, targets, cache, manifest });
    } else {
      fallbackQueue.push({ payload, targets });
    }
  });

  for (const entry of fallbackQueue) {
    const asset = await persistAssetLocally({ payload: entry.payload, getNextFileName });
    if (asset) {
      applyAssetToTargets({ asset, targets: entry.targets, cache, manifest });
    }
  }

  if (manifest.length) {
    const unique = [];
    const seen = new Set();
    manifest.forEach(asset => {
      if (!asset || !asset.photoRef || seen.has(asset.photoRef)) return;
      seen.add(asset.photoRef);
      unique.push(asset);
    });
    parsed.__ssPhotoAssets = unique;
  }

  return {
    json: parsed,
    assets: parsed.__ssPhotoAssets || [],
    assetCache: cache,
  };
};

export const ingestPhotoAssetsIntoLibrary = (setLibrary, assets) => {
  if (typeof setLibrary !== 'function' || !Array.isArray(assets) || !assets.length) {
    return;
  }
  setLibrary(prev => {
    const previous = Array.isArray(prev) ? prev : [];
    const existing = new Set(
      previous
        .map(item => (item && typeof item === 'object' ? item.photoRef || item.id : null))
        .filter(Boolean)
    );
    let changed = false;
    const merged = [...previous];
  assets.forEach(asset => {
      if (!asset || !asset.url) return;
      const key = asset.photoRef || asset.key || asset.url;
      // Skip overlay images or non-user assets
      if (key === 'overlayImage') return;
      // Accept any persisted asset URL (backend/staging may use different dirs).
      // We still skip overlayImage via key above, and UI-level filters in Library
      // hide built-in catalog assets from the visible gallery.
      if (!key || existing.has(key)) return;
      existing.add(key);
      changed = true;
      merged.push({
        id: key,
        photoRef: key,
        remotePhotoId: key,
        cachedRemotePhotoId: key,
        url: asset.url,
        remoteUrl: asset.url,
        originalUrl: asset.url,
        preview: asset.url,
        thumbnailUrl: asset.url,
        persistedAssetUrl: asset.url,
        cachedDataUrl: null,
        cachedAt: Date.now(),
        pinned: Boolean(asset.pinned),
      });
    });
    return changed ? merged : prev;
  });
};
